import * as React from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogForm, DialogHeader,
  DialogTitle, DiscardGuard,
} from "@/components/ui/dialog";
import { Callout, Spinner } from "@/components/ui/feedback";
import { Field } from "@/components/ui/field";
import { Textarea } from "@/components/ui/input";
import { useToast } from "@/components/ui/toast";
import { AlsoBookFor } from "@/features/also-book-for";
import { useBackupTime } from "@/features/callback-backup";
import { useCallbackMinutes } from "@/features/callback-minutes";
import { useCallbackTime } from "@/features/callback-picker";
import { CallbackTimes } from "@/features/callback-times";
import { ymd } from "@/features/day-and-time";
import { alsoBookedMessage, type AlsoBooking } from "@/lib/also-book";
import { ApiError } from "@/lib/api";
import type { PickedTime } from "@/lib/calendar-view";
import { backupTimesOf, callbackNext, callbackWhen, defaultCallback, purposeOf, wallTime, backupGapMinutes } from "@/lib/callbacks";
import { contactName, exitProtected } from "@/lib/contacts";
import { useCallDispositions, useContactOutcome } from "@/lib/queries";
import type { CalendarCallback, Disposition, LeadCard } from "@/lib/types";
import { cn } from "@/lib/utils";

/** What the form opens on: most callbacks are this, whatever order the campaign lists its outcomes in. */
const PLAIN_CALLBACK = "cb";

/**
 * Book a callback on the contact page (ACL-657): the time and backup times of the outcome window,
 * for a contact nobody is on a call with. It records the same outcome through the same route as Set
 * outcome, so the stage, the reminders and the dialer's call all follow as they do from a call.
 *
 * Nobody picks a person, and there may be 100 reps to pick from. The rep says what the callback is
 * for, one of the campaign's callback outcomes, and the form says who takes it: the dialer decides
 * by the stage the outcome moves the lead to (ADR-216 in `platform`).
 */
export function BookCallbackDialog({
  open, onOpenChange, contact, changing = null,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contact: LeadCard;
  /** A callback on the calendar whose time is being changed: the form opens on it (ACL-658). */
  changing?: CalendarCallback | null;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent variant="drawer" size="md">
        <DialogHeader>
          <DialogTitle>{changing ? "Change the callback time" : "Book a callback"}</DialogTitle>
          <DialogDescription>
            {contactName(contact) ?? contact.phone} · the dialer calls at the time you pick, on their clock.
          </DialogDescription>
        </DialogHeader>
        {open && <BookCallbackForm contact={contact} changing={changing} onClose={() => onOpenChange(false)} />}
      </DialogContent>
    </Dialog>
  );
}

/** The form itself, mounted for each booking, so it opens clean. `picked` is a time clicked on the
 *  calendar, which is the same instant on the lead's clock (ACL-659). */
export function BookCallbackForm({
  contact, picked: clicked = null, changing = null, onClose,
}: {
  contact: LeadCard;
  picked?: PickedTime | null;
  changing?: CalendarCallback | null;
  onClose: () => void;
}) {
  const toast = useToast();
  const outcomes = useCallDispositions(contact.calling_campaign?.id ?? null, true);
  const record = useContactOutcome();
  const callback = useCallbackTime(contact.clock_timezone ?? contact.timezone);
  // A callback changed keeps its backup times and its note, for the rep to change too.
  const [keptBackups] = React.useState(() => (changing ? backupTimesOf(changing).map((at) => Date.parse(at)) : []));
  const backup = useBackupTime(callback.value, keptBackups, backupGapMinutes(contact.callback_window_minutes));
  const soon = useCallbackMinutes();
  const [picked, setPicked] = React.useState<number | null>(null);
  const [note, setNote] = React.useState(changing?.note ?? "");
  const [error, setError] = React.useState<string | null>(null);
  // The others in the accident, booked after this one (ACL-671); not when a time is being changed.
  const [also, setAlso] = React.useState<AlsoBooking[]>([]);

  // A rep is not offered what the platform records by itself (ADR-160), such as Callback missed.
  const offered = (outcomes.data ?? []).filter(
    (o: Disposition) => o.kind === "callback" && o.rep_can_pick !== false,
  );
  const usable = offered.filter((o) => exitProtected(contact, o) === null);
  const chosen =
    usable.find((o) => o.id === picked) ??
    usable.find((o) => o.slug === changing?.disposition) ??
    usable.find((o) => o.slug === PLAIN_CALLBACK) ??
    usable[0] ??
    null;
  const backoff = chosen?.backoff_seconds;
  const { start, set } = callback;
  const zone = callback.value.zone;
  const opensAt = changing ? Date.parse(changing.due_at) : clicked?.withTime ? clicked.at.getTime() : null;
  const day = clicked && !clicked.withTime ? clicked.at : null;

  // Opens on the outcome's own wait, as the outcome window does, unless it has a time to open on.
  // A day clicked on the month view takes that day at the time of the wait.
  React.useEffect(() => {
    if (opensAt !== null) set(wallTime(opensAt, zone));
    else if (backoff === undefined) return;
    else if (day) set({ ...wallTime(defaultCallback({ backoff_seconds: backoff }, Date.now()), zone), date: ymd(day) });
    else start({ backoff_seconds: backoff });
  }, [opensAt, day, backoff, zone, start, set]);

  const lead = callback.value.leadClock ? callback.value.zone : null;
  const canSave = chosen !== null && callback.value.iso !== null && backup.ok;

  const save = () => {
    if (!chosen) return;
    setError(null);
    record.mutate(
      {
        leadId: contact.id,
        disposition: chosen.slug,
        note: note.trim() || null,
        callbackAt: callback.value.iso,
        // What it was for stays, words typed included, unless the type changed with the time.
        callbackPurpose: changing?.disposition === chosen.slug && changing.purpose ? changing.purpose : purposeOf(chosen.label),
        callbackBackupTimes: backup.isos,
      },
      {
        onSuccess: async (result) => {
          const when = result.callback_at ? callbackWhen(result.callback_at, lead).there : null;
          const results: { name: string; when: string | null; problem: string | null }[] = [];
          for (const booking of changing ? [] : also) {
            try {
              const booked = await record.mutateAsync({
                leadId: booking.leadId, disposition: chosen.slug, callbackAt: booking.iso,
                callbackPurpose: purposeOf(chosen.label), callbackBackupTimes: [],
              });
              results.push({ name: booking.name, when: booked.callback_at ? callbackWhen(booked.callback_at, lead).there : null, problem: null });
            } catch (e) {
              results.push({ name: booking.name, when: null, problem: e instanceof ApiError ? e.message : "could not be booked." });
            }
          }
          const theirs = alsoBookedMessage(results);
          const mine = when ? `${contactName(contact) ?? contact.phone}, ${when}.` : "";
          if (theirs) toast[theirs.tone](theirs.title, `${mine} ${theirs.detail}`.trim());
          else toast.success(changing ? "Callback time changed" : "Callback booked", mine || undefined);
          onClose();
        },
        onError: (e) => setError(e instanceof ApiError ? e.message : "Could not book the callback."),
      },
    );
  };

  return (
    <DialogForm
      onSubmit={(e) => {
        e.preventDefault();
        if (canSave) save();
      }}
    >
      <DialogBody className="space-y-4">
        <DiscardGuard changed={note.trim() !== (changing?.note ?? "")} />
        {outcomes.isPending ? (
          <div className="flex justify-center py-8">
            <Spinner className="size-4" />
          </div>
        ) : outcomes.error ? (
          <Callout tone="danger">
            {outcomes.error instanceof ApiError ? outcomes.error.message : "Could not load the callback types."}
          </Callout>
        ) : chosen === null ? (
          <Callout tone="warning">
            {offered.length > 0
              ? exitProtected(contact, offered[0]!)
              : "This campaign offers no callback to book. An admin adds one on its Dispositions tab."}
          </Callout>
        ) : (
          <>
            {usable.length > 1 && (
              <fieldset className="space-y-1.5">
                <legend className="text-sm font-medium text-text">What is this callback for</legend>
                <div className="flex flex-wrap gap-2">
                  {usable.map((o) => (
                    <button
                      key={o.id}
                      type="button"
                      aria-pressed={o.id === chosen.id}
                      disabled={record.isPending}
                      onClick={() => setPicked(o.id)}
                      className={cn(
                        "rounded-md border px-3 py-1.5 text-sm",
                        o.id === chosen.id
                          ? "border-accent-8 bg-accent-3 font-medium text-accent-11"
                          : "border-border text-text hover:bg-surface-hover",
                      )}
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
              </fieldset>
            )}
            <CallbackTimes
              leadId={contact.id}
              outcome={chosen.slug}
              callback={callback}
              backup={backup}
              soon={soon}
              disabled={record.isPending}
              windowMinutes={contact.callback_window_minutes}
              // The one being changed is what this replaces, so there is nothing to ask about it.
              open={changing ? null : contact.callback}
            />
            {!changing && (
              <AlsoBookFor leadId={contact.id} caller={contact} outcome={chosen} disabled={record.isPending} onChange={setAlso} />
            )}
            <Field label="Note · kept on the record" htmlFor="book-callback-note">
              <Textarea
                id="book-callback-note"
                rows={3}
                maxLength={4000}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                className="font-sans text-sm"
                placeholder="What was said, and what happens next"
              />
            </Field>
            {callback.value.instant !== null && (
              <p className="text-xs text-text-muted" aria-live="polite">
                <span className="font-medium text-text">Next:</span>{" "}
                {callbackNext(callback.value.instant, lead, backup.isos.length > 0 ? backup.instants : [])}.
              </p>
            )}
          </>
        )}
        {error && <Callout tone="danger">{error}</Callout>}
      </DialogBody>
      <DialogFooter>
        <Button type="button" variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit" variant="default" loading={record.isPending} disabled={!canSave}>
          {changing ? "Change time" : also.length > 0 ? `Book ${also.length + 1} callbacks` : "Book callback"}
        </Button>
      </DialogFooter>
    </DialogForm>
  );
}
